import { closestCalc } from "#src/util/helpers.ts";
import { type Mode, MODES } from "#src/constants/mode.ts";
import { type ProductBase } from "#src/types/products/ProductBase.ts";
import { type ProductType, PRODUCT_TYPE } from "#src/constants/products.ts";

import {
  insuredQuoteData,
  ACCIDENTAL_DEATH_RATES,
  type QuoteParams,
  type QuoteRequestParams,
  type RenderRules,
  type ModeDataItem,
  type ParsingData,
} from "#src/types/QuoteParams.ts";

export interface AccidentalDeathProductType extends ProductBase {
  isAccidentalDeath: boolean;
  faceAmounts: number[];
  eAppParser(
    args: Partial<ParsingData>
  ): AccidentalDeathProductType["defaults"] & {
    face_amount: number;
    mode: Mode;
  };

  defaults: {
    save_age: boolean;
  };
}

function showHealth(conversion: boolean): boolean {
  return !conversion;
}

export function AccidentalDeath(): AccidentalDeathProductType {
  return {
    get type(): ProductType {
      return PRODUCT_TYPE.ACCIDENTAL_DEATH;
    },
    get isAccidentalDeath(): boolean {
      return true;
    },
    get display(): string {
      return "Accidental Death";
    },
    get defaults(): AccidentalDeathProductType["defaults"] {
      return {
        save_age: false,
      };
    },
    get requestCategory(): string {
      return "accidental_death";
    },
    get faceAmounts(): number[] {
      return ACCIDENTAL_DEATH_RATES;
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    renderRules(
      _quoteParams: QuoteParams,
      options = { conversion: false }
    ): Partial<RenderRules> {
      return {
        saveAge: true,
        fixedFaceAmount: true,
        insuredHealth: showHealth(options.conversion),
        mode: true,
        state: true,
      };
    },
    eAppParser(model): ReturnType<AccidentalDeathProductType["eAppParser"]> {
      const faceAmount = closestCalc(model.face_amount, this.faceAmounts);
      let saveAge = model.save_age;
      if (!(typeof saveAge === "boolean")) {
        saveAge = this.defaults.save_age;
      }

      return {
        face_amount: faceAmount || 500000,
        save_age: saveAge,
        mode: model.mode as Mode,
      };
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      return {
        agent: { id: args.agentId },
        compare: false,
        ...insuredQuoteData(args.insured),

        state: args.params.state,
        categories: [this.requestCategory],
        selected_type: args.params.selected_type,

        mode: args.params.mode,
        save_age: args.params.save_age,
        face_amount: args.params.face_amount,
      };
    },
  };
}
